
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";

import {
  getRgbByHue,
  changePicker,
  delayedBoundsCall,
  useMouseEvents
} from "@/components/Reusable/CustomInputs/3rdPartyWrappers/Gradient/helpers";

type Positions = {
  startX: number;
  startY: number;
  positionX: number;
  positionY: number;
};

@Component
export default class Picker extends Vue {
  @Prop()
  red!: number;

  @Prop()
  green!: number;

  @Prop()
  blue!: number;

  @Prop()
  alpha!: number;

  @Prop()
  hue!: number;

  @Prop()
  saturation!: number;

  @Prop()
  value!: number;

  @Prop()
  updateColor!: Function;

  width = 0;
  height = 0;
  pointerWidth = 12;

  mouseEvents: (event: MouseEvent) => void = () => {};

  get offsetLeft() {
    return (((this.saturation * this.width) / 100) | 0) - this.pointerWidth / 2;
  }

  get offsetTop() {
    return (
      ((this.height - (this.value * this.height) / 100) | 0) -
      this.pointerWidth / 2
    );
  }

  get pointerStyle() {
    return {
      backgroundColor: `rgb(${this.red}, ${this.green}, ${this.blue})`,
      left: `${this.offsetLeft}px`,
      top: `${this.offsetTop}px`
    };
  }

  get pickerStyle() {
    const { red, green, blue } = getRgbByHue(this.hue);
    return { backgroundColor: `rgb(${red}, ${green}, ${blue})` };
  }

  mounted() {
    delayedBoundsCall(this.calculateBounds);

    this.mouseEvents = useMouseEvents(
      this.mouseDownHandler,
      this.mouseMoveHandler,
      this.mouseUpHandler
    );
  }

  calculateBounds() {
    const picker = this.$refs.pickerAreaRef as HTMLElement;

    if (picker) {
      this.width = picker.clientWidth;
      this.height = picker.clientHeight;
    }
  }

  clamp(num: number, min: number, max: number) {
    return num <= min ? min : num >= max ? max : num;
  }

  mouseDownHandler(event: MouseEvent) {
    const picker = this.$refs.pickerAreaRef as HTMLElement;
    const { x: elementX, y: elementY } = picker.getBoundingClientRect();
    const startX = event.pageX;
    const startY = event.pageY;
    const positionX = startX - elementX;
    const positionY = startY - elementY;

    const color = changePicker(
      positionX,
      positionY,
      this.height,
      this.width,
      this.hue
    );

    this.updateColor(color, "onStartChange");

    return {
      startX,
      startY,
      positionX,
      positionY
    };
  }

  changeObjectPositions(
    event: MouseEvent,
    { startX, startY, positionX, positionY }: Positions
  ) {
    const moveX = event.pageX - startX;
    const moveY = event.pageY - startY;
    positionX += moveX;
    positionY += moveY;

    const color = changePicker(
      positionX,
      positionY,
      this.height,
      this.width,
      this.hue
    );

    return {
      positions: {
        positionX,
        positionY,
        startX: event.pageX,
        startY: event.pageY
      },
      color
    };
  }

  mouseMoveHandler(
    event: MouseEvent,
    { startX, startY, positionX, positionY }: Positions
  ) {
    event.preventDefault();
    const { positions, color } = this.changeObjectPositions(event, {
      startX,
      startY,
      positionX,
      positionY
    });

    this.updateColor(color, "onChange");

    return positions;
  }

  mouseUpHandler(
    event: MouseEvent,
    { startX, startY, positionX, positionY }: Positions
  ) {
    event.preventDefault();

    const { positions, color } = this.changeObjectPositions(event, {
      startX,
      startY,
      positionX,
      positionY
    });

    this.updateColor(color, "onEndChange");

    return positions;
  }
}
